import React, { useState, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CornerDownRight, ExternalLink, FileCheck, Pin, PinOff, Star, StarOff, Trash2, X as XIcon } from 'lucide-react'
import { ChevronRightIcon } from '../../icons/ChevronRightIcon'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '../../ui/context-menu'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '../../ui/alert-dialog'
import { Button } from '../../ui/Button'
import { useShortcutText } from '../../../hooks/useShortcutText'
import { growMotion } from '../../../lib/motion/growMotion'
import { SidebarItemProps } from './SidebarItem.types'
import './SidebarItem.scss'

/*
  Tab-Knopf (schließen / in neuem Tab öffnen) beim Hover: Er macht in der Reihe
  Platz und schiebt die Zahl nach links, statt über ihr zu liegen. 28 = 24er
  Knopf + 4 Abstand zur Zahl.
  Der Knopf sitzt 3 px vom Rand, die Zeile hat 8 px Innenabstand — die 5 px
  holt ein negativer Rand. Der läuft MIT der Breite: Ein fester Rand sprang
  beim Einblenden und am Ende des Ausblendens auf einen Schlag, die Zahl stieß
  hart zurück.
*/
const grow = growMotion({ axis: 'width', size: 28 })
const ACTION_EDGE = -5
const actionGrow = {
  initial: { ...grow.initial, marginRight: 0 },
  animate: {
    ...grow.animate,
    marginRight: ACTION_EDGE,
    transition: { ...grow.animate.transition, marginRight: (grow.animate.transition as Record<string, unknown>).width },
  },
  exit: {
    ...grow.exit,
    marginRight: 0,
    transition: { ...grow.exit.transition, marginRight: (grow.exit.transition as Record<string, unknown>).width },
  },
  style: grow.style,
} as typeof grow

export const SidebarItem: React.FC<SidebarItemProps> = ({
  icon: Icon,
  label,
  badge,
  isActive = false,
  expandable = false,
  defaultExpanded = false,
  indent = false,
  path,
  hasOpenTab = false,
  isPreview = false,
  isPinned = false,
  hasSubItems = false,
  isSubMenuExpanded = false,
  hasActiveSubItem = false,
  onToggleSubMenu,
  onCloseTab,
  onUnpin,
  onPinTab,
  onOpenNewTab,
  onPromote,
  onDelete,
  onClick,
  onDoubleClick,
  children,
  trailing,
  projectIcon,
  labelColor,
  accentColor,
  depth = 0,
  treeLevel,
  dimmed = false,
  dragging = false,
  dragHandle,
  onAddSubproject,
  deleteDescription,
  favorite = false,
  onFavoriteChange,
}) => {
  const { t } = useTranslation()
  const shortcutText = useShortcutText()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const hasToggledRef = useRef(false)
  const deleteCancelRef = useRef<HTMLButtonElement>(null)

  const handleClick = (e: React.MouseEvent) => {
    if (expandable && children) {
      hasToggledRef.current = true
      setIsExpanded(!isExpanded)
    } else if (onClick) {
      onClick(e)
    }
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!expandable && onDoubleClick) {
      onDoubleClick(e)
    }
  }

  const handleCloseTab = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCloseTab?.()
  }

  const handleOpenNewTab = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpenNewTab?.()
  }

  const showCloseTab = hasOpenTab && !isPinned && !!onCloseTab
  const showOpenTab = !hasOpenTab && !!onOpenNewTab

  // Only show context menu for non-expandable items (leaf items with a path)
  const hasContextMenu = !expandable && !!path

  const itemContent = (
    <div
      ref={dragHandle?.ref}
      className={`sidebar-item ${projectIcon ? 'sidebar-item--project-icon' : ''} ${projectIcon?.glow ? 'sidebar-item--icon-glow' : ''} ${isActive && !hasActiveSubItem ? 'sidebar-item--active' : ''} ${hasActiveSubItem ? 'sidebar-item--has-active-sub' : ''} ${hasSubItems ? 'sidebar-item--has-sub-items' : ''} ${indent ? 'sidebar-item--indent' : ''} ${hasOpenTab ? 'sidebar-item--has-tab' : ''} ${isPreview ? 'sidebar-item--preview' : ''} ${isPinned ? 'sidebar-item--pinned' : ''} ${depth > 0 ? `sidebar-item--depth-${Math.min(depth, 3)}` : ''} ${dimmed ? 'sidebar-item--dimmed' : ''} ${dragging ? 'sidebar-item--dragging' : ''}`}
      role={treeLevel ? 'treeitem' : undefined}
      aria-level={treeLevel}
      aria-expanded={treeLevel && hasSubItems ? isSubMenuExpanded : undefined}
      aria-label={treeLevel ? label : undefined}
      onPointerDown={dragHandle?.onPointerDown}
      data-path={path}
      data-label-color={labelColor ?? undefined}
      data-accent-color={accentColor ?? labelColor ?? undefined}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {expandable && children && (
        <span className={`sidebar-item__chevron ${isExpanded ? 'sidebar-item__chevron--expanded' : ''}`}>
          <ChevronRightIcon size={14} />
        </span>
      )}

      {projectIcon?.glow && (
        <span className="sidebar-item__glow" aria-hidden="true">{projectIcon.glow}</span>
      )}

      {projectIcon ? (
        <span className="sidebar-item__icon sidebar-item__icon--project">{projectIcon.node}</span>
      ) : Icon && (
        <span className="sidebar-item__icon">
          <Icon size={16} />
        </span>
      )}

      {hasSubItems && onToggleSubMenu && (
        <button
          className={`sidebar-item__sub-chevron ${isSubMenuExpanded ? 'sidebar-item__sub-chevron--expanded' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleSubMenu() }}
          aria-label={isSubMenuExpanded ? t('sidebar.collapseSubMenu') : t('sidebar.expandSubMenu')}
        >
          <ChevronRightIcon size={16} />
        </button>
      )}

      <span className="sidebar-item__label">{label}</span>


      {badge !== undefined && badge > 0 && (
        <span className="sidebar-item__badge">{badge > 99 ? '99+' : badge}</span>
      )}

      {/* Beim Anheften / Lösen wächst der Pin wie der Tab-Knopf (`actionGrow`). */}
      <AnimatePresence initial={false}>
        {isPinned && (
          <motion.span
            key="pin"
            className="sidebar-item__action"
            initial={actionGrow.initial}
            animate={actionGrow.animate}
            exit={actionGrow.exit}
            style={actionGrow.style}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="sidebar-item__pin-button"
                  onClick={(e) => { e.stopPropagation(); onUnpin?.() }}
                  aria-label={t('sidebar.unpinTab')}
                >
                  <Pin size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t('sidebar.unpinTab')}</TooltipContent>
            </Tooltip>
          </motion.span>
        )}
      </AnimatePresence>

      {trailing && (
        <span className="sidebar-item__trailing">
          {trailing}
        </span>
      )}

      <AnimatePresence initial={false}>
        {hovered && (showCloseTab || showOpenTab) && (
          <motion.span
            key="action"
            className="sidebar-item__action"
            initial={actionGrow.initial}
            animate={actionGrow.animate}
            exit={actionGrow.exit}
            style={actionGrow.style}
          >
          {showCloseTab && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="sidebar-item__close-tab"
                  onClick={handleCloseTab}
                  aria-label={t('sidebar.closeTab', { shortcut: shortcutText('⌘W') })}
                >
                  <XIcon size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" shortcut={{ kind: 'character', text: 'W', mod: true }}>
                {t('sidebar.contextMenu.closeTab')}
              </TooltipContent>
            </Tooltip>
          )}

          {showOpenTab && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="sidebar-item__open-tab"
                  onClick={handleOpenNewTab}
                  aria-label={t('sidebar.contextMenu.openInNewTab')}
                >
                  <ExternalLink size={16} />
                </button>
              </TooltipTrigger>
              {/* Ohne Kürzel: ⌘+Klick täte hier dasselbe wie der einfache Klick auf den Knopf. */}
              <TooltipContent side="right">
                {t('sidebar.contextMenu.openInNewTab')}
              </TooltipContent>
            </Tooltip>
          )}
          </motion.span>
        )}
      </AnimatePresence>

    </div>
  )

  const contextMenuContent = hasContextMenu ? (
    <ContextMenuContent>
      {!isActive && !hasOpenTab && (
        <ContextMenuItem onSelect={() => onOpenNewTab?.()}>
          <ExternalLink size={14} className="context-menu-content__item-icon" />
          {t('sidebar.contextMenu.openInNewTab')}
        </ContextMenuItem>
      )}
      {hasOpenTab && (
        <>
          {isPreview && onPromote && (
            <ContextMenuItem onSelect={() => onPromote()}>
              <FileCheck size={14} className="context-menu-content__item-icon" />
              {t('sidebar.contextMenu.keepTab')}
            </ContextMenuItem>
          )}
          <ContextMenuItem onSelect={() => isPinned ? onUnpin?.() : onPinTab?.()}>
            {isPinned
              ? <PinOff size={14} className="context-menu-content__item-icon" />
              : <Pin size={14} className="context-menu-content__item-icon" />
            }
            {isPinned ? t('sidebar.contextMenu.unpinTab') : t('sidebar.contextMenu.pinTab')}
          </ContextMenuItem>
          {onCloseTab && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => onCloseTab()}>
                <XIcon size={14} className="context-menu-content__item-icon" />
                {t('sidebar.contextMenu.closeTab')}
                <ContextMenuShortcut>{shortcutText('⌘W')}</ContextMenuShortcut>
              </ContextMenuItem>
            </>
          )}
        </>
      )}
      {onFavoriteChange && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={() => onFavoriteChange(!favorite)}>
            {favorite
              ? <StarOff size={14} className="context-menu-content__item-icon" />
              : <Star size={14} className="context-menu-content__item-icon" />}
            {favorite ? t('favorites.remove') : t('favorites.add')}
          </ContextMenuItem>
        </>
      )}
      {onAddSubproject && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={() => onAddSubproject()}>
            <CornerDownRight size={14} className="context-menu-content__item-icon" />
            {t('sidebar.contextMenu.addSubproject')}
          </ContextMenuItem>
        </>
      )}
      {onDelete && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            className="context-menu-content__item--danger"
            onSelect={() => setDeleteDialogOpen(true)}
          >
            <Trash2 size={14} className="context-menu-content__item-icon" />
            {t('sidebar.contextMenu.deleteProject')}
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  ) : null

  // Kein Tooltip mit dem Label mehr: den gab es nur in der Icon-Leiste, wo das
  // Label ausgeblendet war. Die Sidebar ist jetzt entweder ganz da — samt Label —
  // oder gar nicht (ADR 0005).
  const renderedItem = hasContextMenu ? (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {itemContent}
      </ContextMenuTrigger>
      {contextMenuContent}
    </ContextMenu>
  ) : itemContent

  return (
    <>
      {renderedItem}

      {expandable && isExpanded && children && (
        <div className={`sidebar-item__children ${hasToggledRef.current ? 'sidebar-item__children--animate' : ''}`}>
          {children}
        </div>
      )}

      {onDelete && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          {/* Die Knöpfe sind die `Button`s der App, wie in `ArchiveSectionDialog` — dort steht, warum
              nicht Radix' `Action`/`Cancel`. Schließen und Anfangsfokus übernehmen wir darum selbst. */}
          <AlertDialogContent onOpenAutoFocus={() => deleteCancelRef.current?.focus({ preventScroll: true })}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('project.confirmDelete.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteDescription ?? t('project.confirmDelete.description', { name: label })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} variant="secondary" onClick={() => setDeleteDialogOpen(false)}>
                {t('project.confirmDelete.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteDialogOpen(false)
                  onDelete()
                }}
              >
                {t('project.confirmDelete.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}
